// Praktikanten Page - Verwaltung von Praktikanten
import React, { useState, useEffect } from 'react';
import { Users, Plus, Calendar, User, Trash2, Edit, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { praktikantenApi, usersApi, wacheApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const typLabels = {
  RS_PRAKTIKANT: 'RS-Praktikant',
  TRUPP_MANN: 'Trupp-Mann',
  SCHUELER: 'Schüler',
  SONSTIGER: 'Sonstiger'
};

export default function PraktikantenPage() {
  const { user } = useAuthStore();
  const [praktikanten, setPraktikanten] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [abteilungen, setAbteilungen] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPraktikant, setEditingPraktikant] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [praktikantenRes, mitarbeiterRes, abteilungenRes] = await Promise.all([
        praktikantenApi.getAll(false),
        usersApi.getPraktikantenbetreuer(),
        wacheApi.getAbteilungen()
      ]);
      setPraktikanten(praktikantenRes.data || []);
      setMitarbeiter(mitarbeiterRes.data || []);
      setAbteilungen(abteilungenRes.data || []);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (praktikant) => {
    setEditingPraktikant(praktikant);
    setShowDialog(true);
  };

  const handleDelete = async (praktikant) => {
    if (!confirm(`Praktikant ${praktikant.vorname} ${praktikant.nachname} wirklich löschen?`)) return;
    
    try {
      await praktikantenApi.delete(praktikant.id);
      toast.success('Gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const aktive = praktikanten.filter(p => p.ist_aktiv);
  const zukunftig = praktikanten.filter(p => !p.ist_aktiv && p.zeitraum_von > new Date().toISOString().split('T')[0]);
  const vergangen = praktikanten.filter(p => !p.ist_aktiv && p.zeitraum_bis < new Date().toISOString().split('T')[0]);

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <GraduationCap className="w-8 h-8" />
              Praktikanten
            </h1>
            <p className="text-muted-foreground mt-1">
              Verwaltung von Praktikanten, Schülern und Auszubildenden
            </p>
          </div>
          <Button onClick={() => { setEditingPraktikant(null); setShowDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Hinzufügen
          </Button>
        </div>

        {/* Statistik */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Aktuell</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{aktive.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Zukünftig</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{zukunftig.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Vergangen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-600">{vergangen.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktive Praktikanten */}
        <Card>
          <CardHeader>
            <CardTitle>Aktive Praktikanten ({aktive.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {aktive.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Keine aktiven Praktikanten</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Zeitraum</TableHead>
                    <TableHead>Abteilung</TableHead>
                    <TableHead>Betreuer</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aktive.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.vorname} {p.nachname}</TableCell>
                      <TableCell><Badge>{typLabels[p.typ]}</Badge></TableCell>
                      <TableCell className="text-sm">
                        {new Date(p.zeitraum_von).toLocaleDateString('de-DE')} - {new Date(p.zeitraum_bis).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell className="text-sm">{p.abteilung_name}</TableCell>
                      <TableCell className="text-sm">{p.betreuer_name || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(p)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(p)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Zukünftige Praktikanten */}
        {zukunftig.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Zukünftige Praktikanten ({zukunftig.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {zukunftig.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.vorname} {p.nachname}</TableCell>
                      <TableCell><Badge variant="outline">{typLabels[p.typ]}</Badge></TableCell>
                      <TableCell className="text-sm">ab {new Date(p.zeitraum_von).toLocaleDateString('de-DE')}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(p)}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(p)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Vergangene Praktikanten */}
        {vergangen.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Vergangene Praktikanten ({vergangen.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {vergangen.map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.vorname} {p.nachname}</TableCell>
                      <TableCell><Badge variant="secondary">{typLabels[p.typ]}</Badge></TableCell>
                      <TableCell className="text-sm">
                        {new Date(p.zeitraum_von).toLocaleDateString('de-DE')} - {new Date(p.zeitraum_bis).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell className="text-sm">{p.abteilung_name}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(p)}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(p)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <PraktikantDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        praktikant={editingPraktikant}
        mitarbeiter={mitarbeiter}
        abteilungen={abteilungen}
        onSuccess={loadData}
      />
    </MainLayout>
  );
}

function PraktikantDialog({ open, onOpenChange, praktikant, mitarbeiter, abteilungen, onSuccess }) {
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    typ: 'RS_PRAKTIKANT',
    anwaerter_jahr: '',
    zeitraum_von: '',
    zeitraum_bis: '',
    abteilung_id: '',
    betreuer_id: '',
    telefon: '',
    email: '',
    adresse: '',
    notizen: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (praktikant) {
      setFormData({
        vorname: praktikant.vorname || '',
        nachname: praktikant.nachname || '',
        typ: praktikant.typ || 'RS_PRAKTIKANT',
        anwaerter_jahr: praktikant.anwaerter_jahr || '',
        zeitraum_von: praktikant.zeitraum_von || '',
        zeitraum_bis: praktikant.zeitraum_bis || '',
        abteilung_id: praktikant.abteilung_id || '',
        betreuer_id: praktikant.betreuer_id || '',
        telefon: praktikant.telefon || '',
        email: praktikant.email || '',
        adresse: praktikant.adresse || '',
        notizen: praktikant.notizen || ''
      });
    } else {
      setFormData({
        vorname: '',
        nachname: '',
        typ: 'RS_PRAKTIKANT',
        anwaerter_jahr: '',
        zeitraum_von: '',
        zeitraum_bis: '',
        abteilung_id: abteilungen[0]?.id || '',
        betreuer_id: '',
        telefon: '',
        email: '',
        adresse: '',
        notizen: ''
      });
    }
  }, [praktikant, open, abteilungen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Leeren String für anwaerter_jahr → null senden (Backend erwartet enum oder null)
      const payload = {
        ...formData,
        anwaerter_jahr: formData.anwaerter_jahr || null,
      };
      if (praktikant) {
        await praktikantenApi.update(praktikant.id, payload);
        toast.success('Aktualisiert');
      } else {
        await praktikantenApi.create(payload);
        toast.success('Erstellt');
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{praktikant ? 'Praktikant bearbeiten' : 'Neuer Praktikant'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vorname *</Label>
              <Input required value={formData.vorname} onChange={(e) => setFormData(p => ({ ...p, vorname: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Nachname *</Label>
              <Input required value={formData.nachname} onChange={(e) => setFormData(p => ({ ...p, nachname: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Typ *</Label>
            <Select value={formData.typ} onValueChange={(v) => setFormData(p => ({ ...p, typ: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(typLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Anwärter-Jahr (Schulterstück)</Label>
            <Select
              value={formData.anwaerter_jahr || '__none__'}
              onValueChange={(v) => setFormData(p => ({ ...p, anwaerter_jahr: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger data-testid="praktikant-anwaerter-jahr-trigger">
                <SelectValue placeholder="Kein Schulterstück" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Kein Schulterstück</SelectItem>
                <SelectItem value="BM-A1">Anwärter Jahr 1 (BM-A1)</SelectItem>
                <SelectItem value="BM-A2">Anwärter Jahr 2 (BM-A2)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Bestimmt das angezeigte Schulterstück auf dem Magnetboard.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Von *</Label>
              <Input type="date" required value={formData.zeitraum_von} onChange={(e) => setFormData(p => ({ ...p, zeitraum_von: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Bis *</Label>
              <Input type="date" required value={formData.zeitraum_bis} onChange={(e) => setFormData(p => ({ ...p, zeitraum_bis: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Abteilung *</Label>
            <Select value={formData.abteilung_id} onValueChange={(v) => setFormData(p => ({ ...p, abteilung_id: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {abteilungen.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Betreuer</Label>
            <Select value={formData.betreuer_id} onValueChange={(v) => setFormData(p => ({ ...p, betreuer_id: v }))}>
              <SelectTrigger><SelectValue placeholder={mitarbeiter.length === 0 ? 'Kein Praktikantenbetreuer gefunden' : 'Optional…'} /></SelectTrigger>
              <SelectContent>
                {mitarbeiter.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.dienstgrad ? `${m.dienstgrad} ` : ''}{m.vorname} {m.nachname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {mitarbeiter.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Es sind noch keine Mitarbeiter mit dem Tag "Praktikantenbetreuer" markiert.
                Tag zuerst im Mitarbeiter-Profil vergeben.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input value={formData.telefon} onChange={(e) => setFormData(p => ({ ...p, telefon: e.target.value }))} />
          </div>

          <div className="space-y-2">
            <Label>E-Mail</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} />
          </div>

          <div className="space-y-2">
            <Label>Adresse</Label>
            <Input value={formData.adresse} onChange={(e) => setFormData(p => ({ ...p, adresse: e.target.value }))} />
          </div>

          <div className="space-y-2">
            <Label>Notizen</Label>
            <Input value={formData.notizen} onChange={(e) => setFormData(p => ({ ...p, notizen: e.target.value }))} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" disabled={isLoading}>{isLoading ? 'Wird gespeichert...' : 'Speichern'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
